import { useEffect, useState } from "react";
import { Check, HardHat } from "lucide-react";
import { Button, Input, TechnicianAttachmentList } from "../../../shared/ui";
import { splitOversized } from "../../../shared/lib/uploadLimits";
import type { TechnicianFeedbackController } from "../model/types";
import { AddFilesButton } from "./AddFilesButton";

type Props = {
  controller: TechnicianFeedbackController;
};

/** How long "Gespeichert" stays up after a comment save. */
const SAVED_FLASH_MS = 2500;

/**
 * "Rückmeldung an das Büro" — the one card on the worker pages that is *not* read-only: the
 * technician's comment (saved on its own button, so a half-typed note never goes out on blur)
 * and the files they sent, with an X only on the rows they may remove. The sign-off sheets
 * write the same comment and the same files; this card is for during the job and after it.
 */
export const TechnicianFeedbackCard = ({ controller }: Props) => {
  const {
    comment,
    attachments,
    canRemove,
    saveComment,
    isSavingComment,
    commentError,
    addFiles,
    isUploading,
    uploadError,
    removeAttachment,
    removingId,
    removeError,
  } = controller;

  const [draft, setDraft] = useState(comment);
  const [savedFlash, setSavedFlash] = useState(false);
  const [sizeError, setSizeError] = useState<string | null>(null);

  // A refetch (or the sign-off sheet writing the same column) updates the stored value; adopt
  // it unless the worker is mid-edit — their typing wins over a background refresh.
  const dirty = draft.trim() !== comment.trim();
  useEffect(() => {
    if (!dirty) setDraft(comment);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comment]);

  useEffect(() => {
    if (!savedFlash) return;
    const timer = window.setTimeout(() => setSavedFlash(false), SAVED_FLASH_MS);
    return () => window.clearTimeout(timer);
  }, [savedFlash]);

  const onSave = async () => {
    await saveComment(draft);
    setSavedFlash(true);
  };

  const onPick = async (picked: File[]) => {
    const { accepted, message } = splitOversized(picked);
    setSizeError(message);
    await addFiles(accepted);
  };

  const fileError = sizeError ?? uploadError ?? removeError;

  return (
    <section
      aria-label="Rückmeldung an das Büro"
      className="rounded-2xl border border-slate-200 bg-white p-4"
    >
      <div className="flex items-center gap-2">
        <HardHat size={16} className="text-slate-500" aria-hidden />
        <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
          Rückmeldung an das Büro
        </p>
      </div>

      <div className="mt-3">
        <Input
          as="textarea"
          value={draft}
          onChange={setDraft}
          placeholder="Hinweis für das Büro – was wurde gemacht, was fehlt noch?"
          inputClassName="!min-h-[88px]"
          disabled={isSavingComment}
          aria-label="Kommentar an das Büro"
        />
        <div className="mt-2 flex items-center justify-between gap-3">
          <span className="text-xs text-slate-500" aria-live="polite">
            {commentError ? (
              <span className="text-red-500">{commentError}</span>
            ) : savedFlash && !dirty ? (
              <span className="inline-flex items-center gap-1 text-emerald-600">
                <Check size={14} aria-hidden /> Gespeichert
              </span>
            ) : null}
          </span>
          <Button
            size="sm"
            fullWidth={false}
            onClick={onSave}
            isLoading={isSavingComment}
            disabled={!dirty}
            className="!text-sm"
          >
            Kommentar speichern
          </Button>
        </div>
      </div>

      <div className="mt-4">
        <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
          Dateien ({attachments.length})
        </p>
        <TechnicianAttachmentList
          items={attachments}
          canRemove={canRemove}
          onRemove={(item) => void removeAttachment(item)}
          removingId={removingId}
          className="mt-2"
        />
        <div className="mt-3">
          <AddFilesButton onPick={onPick} isUploading={isUploading} />
        </div>
        {fileError && (
          <p role="alert" className="mt-2 text-xs text-red-500">
            {fileError}
          </p>
        )}
      </div>
    </section>
  );
};
